"use client";

import { FC, useState } from "react";
import { showToast } from "@/utils/showToast";
import AnimatedCard from "./AnimatedCard";
import BigButton from "@/components/shared/card/BigButton";
import CurrencyForm from "@/pages/Sniper/components/CurrencyForm";
import InfoBox from "./InfoBox";
import SnipeDetailsGrid from "@/pages/Sniper/components/SnipeDetailsGrid";
import { shortenAddress } from "@utils/address";
// Define the props interface
import type { IAsset } from "@/api/IWalletController";
import IconButton from "@components/shared/card/IconButton";
import IconButtonsList from "@components/shared/card/IconsList";

export interface MemeSwapCardProps {
    className?: string;
    tokenName: string | null;
    tokenAddress: string;
    initialAmount: string | number;
    profitPercentage: string | number;
    priceInTon?: string;
    age: number | string;
    sendAmount: string | number;
    setSendAmount: (value: string) => void;
    balance: string | number;
    usdPrice: string | number;
    isSellDisabled: boolean;
    status: "Active" | "Cancelled" | "In Progress" | "Success" | "Failed" | "Rejected" | "Migrated/Unknown";
    assetOutInfo?: IAsset;
    assetInInfo?: IAsset;
    revealed?: boolean;
    updateSnipe?: () => Promise<void> | void;
    collapseSnipe?: () => Promise<void> | void;
    guid?: string;
    hide?: boolean;
    collapsed?: boolean;
}

const MemeSwapCard: FC<MemeSwapCardProps> = (props) => {
    // Defensive: If asset info is missing, do not render
    if (!props.assetInInfo || !props.assetInInfo.guid || !props.assetOutInfo || !props.assetOutInfo.guid) {
        return null;
    }

    // Destructure props only after guard
    const {
        assetInInfo,
        assetOutInfo,
        className,
        tokenName,
        tokenAddress,
        initialAmount,
        profitPercentage,
        priceInTon,
        age,
        sendAmount,
        setSendAmount,
        balance,
        usdPrice,
        isSellDisabled,
        status,
        revealed = true,
        updateSnipe,
        guid,
        hide = false,
        collapsed = false,
    } = props;

    const tokenAddressShort = shortenAddress(tokenAddress || "");
    const [isCollapsed, setIsCollapsed] = useState<boolean>(!!collapsed);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /**
     * Handles the meme swap sell action for this card.
     * Extracted from the inline BigButton onClick for clarity and maintainability.
     */
    const handleSellClick = async () => {
        if (!sendAmount || isNaN(Number(sendAmount)) || Number(sendAmount) <= 0) {
            showToast("Please enter a valid amount before swapping.", "error");
            return;
        }
        try {
            setIsLoading(true);
            const { useMemeSwap } = await import("@/stores/meme-swap.store");
            const { initialAssets } = await import("@/stores/assets.store");
            const tonAsset = initialAssets.find((a) => a.symbol === "TON");

            console.log("MemeSwapCard assets:", {
                assetOutInfo,
                assetInInfo,
                tonAsset,
                sendAmount,
            });

            if (!tonAsset || !tonAsset.guid) {
                showToast("TON asset not found for meme swap", "error");
                console.error("TON asset not found");
                return;
            }

            if (!assetOutInfo || !assetOutInfo.guid) {
                console.error("Token asset not found");
                if (guid) {
                    showToast("Waiting for token data to load...", "info");
                    setTimeout(async () => {
                        try {
                            if (assetOutInfo && assetOutInfo.guid) {
                                console.log("Token data loaded, retrying swap");
                                const button = document.querySelector(
                                    '[data-sell-button="' + guid + '"]',
                                ) as HTMLButtonElement;
                                if (button) button.click();
                            } else {
                                showToast("Token asset not found for meme swap", "error");
                                setIsLoading(false);
                            }
                        } catch (err) {
                            console.error("Error refreshing token data", err);
                            showToast("Token asset not found for meme swap", "error");
                            setIsLoading(false);
                        }
                    }, 1500);
                    return;
                } else {
                    showToast("Token asset not found for meme swap", "error");
                    setIsLoading(false);
                    return;
                }
            }

            const inputAsset = assetOutInfo;
            if (inputAsset.symbol === "TON") {
                showToast("TON asset or input asset not found for meme swap", "error");
                console.warn("TON asset or input asset not found for meme swap");
                return;
            }

            // Set up the swap
            useMemeSwap.getState().setAssetIn(inputAsset);
            useMemeSwap.getState().setAssetOut(tonAsset);
            useMemeSwap.getState().setAssetInAmount(String(sendAmount));
            useMemeSwap.getState().setMemeSwapTargetAddress(tonAsset.guid);

            try {
                const isVerifiedToken = inputAsset.isVerified || false;
                const swapType = isVerifiedToken ? "internal" : "external";
                console.log(`Executing ${swapType} swap for token:`, {
                    symbol: inputAsset.symbol,
                    isVerified: isVerifiedToken,
                });
                const result = await useMemeSwap.getState().executeSwap(swapType);
                if (result && result.status === "success") {
                    showToast("Meme swap executed successfully!", "success");
                    console.log("Meme swap executed", result);
                } else {
                    showToast("Meme swap failed.", "error");
                    console.error("Meme swap failed", result);
                }
            } catch (error) {
                showToast("Meme swap execution error", "error");
                console.error("Meme swap execution error", error);
            } finally {
                setIsLoading(false);
            }
        } catch (err) {
            setIsLoading(false);
            console.error("Meme swap failed", err);
        }
    };

    return (
        <AnimatedCard
            revealed={revealed}
            hide={hide}
            collapsed={isCollapsed}
            status={status}
            guid={guid}
            tokenAddress={tokenAddress}
            className={className}
        >
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="font-bold">{tokenName || ""}</span>
                    <span className="text-gray-300 text-sm">{tokenAddressShort}</span>
                </div>
                <IconButtonsList>
                    {status !== "Failed" && status !== "Cancelled" ? (
                        <IconButton type="Reload" onClick={updateSnipe} />
                    ) : (
                        <>
                            <IconButton
                                type="ChevronDown"
                                onClick={() => setIsCollapsed((c: boolean) => !c)}
                                className={isCollapsed ? "" : "-rotate-180"}
                            />
                        </>
                    )}
                </IconButtonsList>
            </div>

            {/* MEMEPAD TOKEN INFO */}

            {/* Info Boxes - Use props */}
            <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                    <InfoBox label="Sent:" value={initialAmount} image={assetInInfo?.image} />
                    {assetOutInfo && <InfoBox label="Asset:" value={assetOutInfo.symbol} image={assetOutInfo.image} />}
                    <InfoBox label="Profit:" value={`${profitPercentage}%`} />
                </div>
            </div>

            <SnipeDetailsGrid price={priceInTon ?? "N/A"} priceTrend="none" marketCap="N/A" age={age} />

            <CurrencyForm
                title="Send"
                amount={sendAmount}
                setAmount={setSendAmount}
                balance={balance}
                price={typeof usdPrice === "number" ? usdPrice : Number(usdPrice) || 0}
            />

            {/* Button */}
            <BigButton
                text={isLoading ? "Loading..." : isSellDisabled ? "Set amount" : "Sell"}
                data-sell-button={guid}
                onClick={handleSellClick}
                variant="default"
                disabled={isSellDisabled}
            />
        </AnimatedCard>
    );
};

export default MemeSwapCard;
